import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { SingleComponent } from "./layouts/single/single.component";
import { MainComponent } from "./layouts/main/main.component";
import { AuthGuard } from "./auth/authguard.guard";
import { AdminAuthGuard } from "./auth/adminAuthguard.guard";
import { SalesMailAccessGuard } from "./auth/salesmail-access.guard";
import { NotFoundComponent } from "./not-found/not-found.component";

const routes: Routes = [
  {
    path: "",
    component: MainComponent,
    canActivate: [AuthGuard],
    // Gates every child below for accounts without SalesMail access. Declared
    // on the shell rather than per route so new routes are covered by default;
    // the guard itself lets "no-access" through.
    canActivateChild: [SalesMailAccessGuard],
    children: [
      {
        // Shown instead of the dashboard when the account has no SalesMail
        // access. Lives inside the shell so the (disabled) nav stays visible.
        // Declared before the empty-path route: the recognizer tries children in
        // order, and an empty-path lazy route would otherwise pull down the
        // whole DashboardModule chunk before failing to match and backtracking.
        path: 'no-access',
        loadComponent: () => import('./no-access/no-access.component').then(m => m.NoAccessComponent)
      },
      {
        path: "",
        loadChildren: () => import("./dashboard/dashboard.module").then(m => m.DashboardModule),
        // canActivate: [AdminAuthGuard],
      },
      {
        path: "results",
        loadChildren: () => import("./results/results.module").then(m => m.ResultsModule),
        canActivate: [AdminAuthGuard],
      },
      {
        path: "reports",
        loadChildren: () => import("./reports/reports.module").then(m => m.ReportsModule),
        // canActivate: [AdminAuthGuard],
      },
      {
        path: "users",
        loadChildren: () => import("./users/users.module").then(m => m.UsersModule),
        canActivate: [AdminAuthGuard],
      },
      {
        path: "settings",
        loadChildren: () => import("./settings/settings.module").then(m => m.SettingsModule),
        canActivate: [AdminAuthGuard],
      },
      { 
        path: "record", 
        loadChildren: () => import("./record/record.module").then(m => m.RecordModule) 
      },
      {
        path: "snap",
        loadChildren: () => import('./snap/snap.module').then(m => m.SnapModule)
      },
      { 
        path: "share", 
        loadChildren: () => import("./share/share.module").then(m => m.ShareModule) 
      },
      {
        path: "my-videos",
        loadChildren: () => import("./my-results/my-results.module").then(m => m.MyResultsModule),
      },
      { 
        path: "feed", 
        loadChildren: () => import("./feed/feed.module").then(m => m.FeedModule) 
      },
      { 
        path: 'ctas', 
        loadChildren: () => import('./ctas/ctas.module').then(m => m.CtasModule) 
      },
      { 
        path: 'qrpro', 
        loadChildren: () => import('./qrpro/qrpro.module').then(m => m.QrproModule) 
      },
      {
        path: 'my-account',
        loadComponent: () => import('./preferences/preferences.component').then(m => m.PreferencesComponent)
      },
      {
        path: 'crm',
        loadComponent: () => import('./crm/crm-search/crm-search.component').then(m => m.CrmSearchComponent)
      }, 
      {
        path: 'script-library',
        loadComponent: () => import('./script-library/script-library.component').then(m => m.ScriptLibraryComponent)
      },
      {
        path: 'video-library',
        loadComponent: () => import('./video-library/video-library.component').then(m => m.VideoLibraryComponent)
      }
    ],
  },
  {
    path: "outlook",
    canActivate: [AuthGuard, SalesMailAccessGuard],
    loadComponent: () => import('./outlook-plugin/outlook-plugin.component').then(m => m.OutlookPluginComponent)
  },
  {
    path: "chrome-extension",
    canActivate: [AuthGuard, SalesMailAccessGuard],
    loadComponent: () => import('./chrome-plugin/chrome-plugin.component').then(m => m.ChromePluginComponent)
  },
  {
    path: "signin",
    component: SingleComponent,
    loadChildren: () => import("./auth/auth.module").then(m => m.AuthModule),
  },
  {
    path: "sso/:provider",
    loadComponent: () => import("./auth/sso-endpoint/sso-endpoint.component").then(m => m.SsoEndpointComponent),
  },
  {
    // Outside the shell, so the shell's canActivateChild does not cover it. A
    // blocked user typing any unmatched URL would otherwise land on a nav-less
    // dead end. Signed-out visitors still get NotFound: the guard defers when
    // there is no session rather than redirecting them into the shell.
    path: "**",
    component: NotFoundComponent,
    canActivate: [SalesMailAccessGuard],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
